import { useTranslation } from 'react-i18next'
import type { Tenant } from '@/types/auth'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'

interface TenantCardProps {
  tenant: Tenant
  onEdit?: (tenant: Tenant) => void
  onDelete?: (tenant: Tenant) => void
}

const statusColors: Record<Tenant['status'], string> = {
  active: 'bg-green-100 text-green-800',
  suspended: 'bg-yellow-100 text-yellow-800',
  pending: 'bg-blue-100 text-blue-800',
  cancelled: 'bg-red-100 text-red-800',
}

export function TenantCard({ tenant, onEdit, onDelete }: TenantCardProps) {
  const { t, i18n } = useTranslation()

  const statusLabels: Record<Tenant['status'], string> = {
    active: t('admin.tenants.status.active'),
    suspended: t('admin.tenants.status.suspended'),
    pending: t('admin.tenants.status.pending'),
    cancelled: t('admin.tenants.status.cancelled'),
  }

  const formattedDate = new Date(tenant.created_at).toLocaleDateString(i18n.language === 'pt' ? 'pt-PT' : 'en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            {tenant.logo_url ? (
              <img
                src={tenant.logo_url}
                alt={`${tenant.name} logo`}
                className="h-10 w-10 rounded-md object-cover flex-shrink-0"
              />
            ) : (
              <div className="h-10 w-10 rounded-md bg-muted flex items-center justify-center flex-shrink-0">
                <span className="text-lg font-semibold text-muted-foreground">
                  {tenant.name.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
            <div className="min-w-0">
              <CardTitle className="text-base truncate">{tenant.name}</CardTitle>
              <CardDescription className="truncate">{tenant.slug}</CardDescription>
            </div>
          </div>
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium flex-shrink-0 ${statusColors[tenant.status]}`}
          >
            {statusLabels[tenant.status]}
          </span>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-2 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">{t('admin.tenants.schema')}</span>
            <code className="text-xs bg-muted px-2 py-1 rounded">
              {tenant.schema_name}
            </code>
          </div>
          {tenant.domain && (
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">{t('admin.tenants.domain')}</span>
              <span className="truncate max-w-[200px]">{tenant.domain}</span>
            </div>
          )}
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">{t('admin.tenants.createdAt')}</span>
            <span>{formattedDate}</span>
          </div>
        </div>
        {(onEdit || onDelete) && (
          <div className="mt-4 pt-4 border-t flex gap-2">
            {onEdit && (
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={() => onEdit(tenant)}
              >
                {t('common.edit')}
              </Button>
            )}
            {onDelete && (
              <Button
                variant="outline"
                size="sm"
                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                onClick={() => onDelete(tenant)}
              >
                {t('common.delete')}
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
